/**
 * CSV formula-injection guard.
 *
 * Exported reports contain customer-supplied free text (`customer_name`,
 * `special_requests`, addresses). Excel, Google Sheets and LibreOffice treat a
 * cell beginning with `=`, `+`, `-`, `@`, TAB or CR as a FORMULA, so a value
 * like `=cmd|' /C calc'!A0` executes rather than displays.
 *
 * The rule below deliberately does NOT neutralise genuine negative numbers:
 * `-50.00` must stay a number, or every refund and discount column in the
 * export becomes text and breaks the operator's spreadsheet maths. So a leading
 * `-` is only treated as dangerous when what follows is not a plain number.
 */

/** Leading characters a spreadsheet may interpret as the start of a formula. */
const FORMULA_PREFIXES = ['=', '+', '@', '\t', '\r']

/** A value that is unambiguously numeric — safe to leave alone. */
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/

/**
 * Neutralise a single CSV cell value.
 *
 * Dangerous values are prefixed with a single quote, which spreadsheets treat as
 * "the rest is literal text" and strip on display.
 *
 * Non-string input is returned as an empty string for null/undefined and
 * stringified otherwise, so callers never emit the literal text "undefined"
 * into a report.
 */
export function sanitizeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return ''

  const str = typeof value === 'string' ? value : String(value)
  if (str.length === 0) return ''

  // Real numbers (including negatives) pass through untouched.
  if (PLAIN_NUMBER.test(str)) return str

  const first = str[0]

  if (FORMULA_PREFIXES.includes(first)) return `'${str}`

  // A leading '-' that is not a plain number: e.g. "-=1+1" or "-cmd".
  if (first === '-') return `'${str}`

  return str
}
